'use client';

import { useEffect } from 'react';
import { reportClientError, toClientErrorReport } from '@tourtask/utils/client/reportClientError';

/**
 * Root error boundary. Catches throws originating in the root layout / providers
 * themselves (which a route-level `error.tsx` cannot), so it must be fully
 * self-contained: it replaces the entire document and renders WITHOUT the app's
 * providers, theme, or translations — any of which may be the very thing that
 * threw. Hence plain markup + inline styles and no `next-intl`/MUI.
 */
export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    reportClientError(toClientErrorReport(error, { kind: 'global-error' }));
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
          background: '#f4f5f7',
          color: '#1a1a1a',
        }}
      >
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              maxWidth: 520,
              width: '100%',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
              padding: '40px 32px',
              textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 12px' }}>Something went wrong</h1>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: '#555', margin: '0 0 24px' }}>
              The page hit an unexpected error and couldn&rsquo;t finish loading. Reloading usually
              fixes it &mdash; if it keeps happening, sign in again.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  background: '#1976d2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 22px',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Reload
              </button>
              {/* Escape hatch for a deterministic crash in the authenticated chrome
                  (where Reload would just loop): go to the public login route, which
                  doesn't mount that chrome, so the user can re-authenticate instead
                  of being stuck. A plain link, so a full page load: nothing of the
                  crashed tree carries over, as it would with a router navigation. */}
              <a
                href="/login"
                style={{
                  display: 'inline-block',
                  background: '#fff',
                  color: '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: 8,
                  padding: '10px 22px',
                  fontSize: 15,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Sign in again
              </a>
            </div>
            {error?.digest || process.env.NEXT_PUBLIC_DEPLOY_ID ? (
              <p style={{ fontSize: 12, color: '#999', marginTop: 20 }}>
                {error?.digest ? <>Reference: {error.digest}</> : null}
                {error?.digest && process.env.NEXT_PUBLIC_DEPLOY_ID ? ' · ' : null}
                {process.env.NEXT_PUBLIC_DEPLOY_ID ? (
                  <>Build: {process.env.NEXT_PUBLIC_DEPLOY_ID.slice(0, 8)}</>
                ) : null}
              </p>
            ) : null}
          </div>
        </div>
      </body>
    </html>
  );
}
